/**
* @author ayou[beica1@outook.com]
* @date 2022/1/4 14:26
* @description
*   TheMissionCard.vue of WeTrade
*/
<template>
<div v-if="pending" class="mission br-lg px-12">
  <component :is="mission" @forbiddenClose="useClose = false" @done="refresh" />
  <span v-if="useClose" class="close" @click="next()">
    <Icon name="close" />
  </span>
</div>
</template>

<script lang="ts">
import Icon from '@/components/Icon.vue'
import { events } from '@/config'
import { produceMission } from '@/modules/home/home'
import { readTasks, Task, TaskType } from '@/modules/home/home.api'
import ForGuest from '@/modules/home/missions/ForGuest.vue'
import { isLogged } from '@/state'
import { off, on } from 'essential/tools/event'
import { Component, defineComponent, onActivated, onBeforeUnmount, shallowRef, watch } from 'vue'
import * as R from 'ramda'
import { YesOrNo } from '@/types'

export default defineComponent({
  name: 'TheMissionCard',
  components: { Icon },
  emits: ['dismiss', 'show', 'collect'],
  setup (props, ctx) {
    const useClose = shallowRef(true)
    const pending = shallowRef(false)
    const mission = shallowRef<Component | null>(null)
    let tasks: Task[] = [] // 可用任务列表
    const closedTasks: Task[] = [] // 关闭的任务列表

    watch(mission, () => {
      useClose.value = true
    })

    const dismiss = () => {
      ctx.emit('dismiss')
    }

    const showUp = () => {
      ctx.emit('show')
    }

    const displayTask = (tasks: Task[]) => {
      if (R.isEmpty(tasks)) {
        return dismiss()
      }

      showUp()

      const usableTask = tasks[0]

      pending.value = Boolean(usableTask.type)

      if (pending.value) {
        mission.value = produceMission(usableTask)
      }

      // 任务组件解析失败 不显示
      if (mission.value === null) {
        pending.value = false
      }
    }

    const next = () => {
      const task = tasks.shift()
      if (task) {
        closedTasks.push(task)
      }
      displayTask(tasks)
    }

    const refresh = () => {
      if (!isLogged()) {
        mission.value = ForGuest
        pending.value = true
      } else {
        readTasks().then(resp => {
          // 不显示用户关闭的task
          tasks = R.differenceWith(R.eqProps('type'), resp, closedTasks)
          displayTask(tasks)
          checkIsFinishDemoTrade(tasks)
        })
      }
    }

    // 判断用户是否已经完成demo下单 任务
    const checkIsFinishDemoTrade = (tasks: Task[]) => {
      if (tasks.length === 0) {
        return false
      } else {
        for (let i = 0; i < tasks.length; i++) {
          if (tasks[i].type === TaskType.ROOKIE) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const noviceTasks = tasks[i].noviceTasks
            for (let j = 0; j < noviceTasks.length; j++) {
              if (noviceTasks[j].tag === YesOrNo.YES && noviceTasks[j].status === YesOrNo.YES) {
                ctx.emit('collect', true)
                return
              }
            }
          }
        }
      }
    }

    const restoreAndRefresh = () => {
      pending.value = false
      mission.value = null
      refresh()
    }

    on([events.login, events.logout], restoreAndRefresh)

    onActivated(() => {
      refresh()
    })

    onBeforeUnmount(() => {
      off([events.login, events.logout], restoreAndRefresh)
    })

    return {
      refresh,
      pending,
      mission,
      useClose,
      next,
    }
  },
})
</script>

<style scoped lang="scss">
.mission {
  position: relative;
  padding-top: 40px;
  background: var(--color-light);
}

.close {
  position: absolute;
  top: 8px;
  right: 8px;

  .icon-close {
    border-radius: 50%;
    background: var(--color-white);
    color: var(--color-tip);
  }
}
</style>
